import { useState } from "react";
import { motion } from "framer-motion";
import { Egg, IceCream2, X as XIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EGG_OPTIONS, SCOOP_OPTIONS, PURSE_TYPES, DESIGNERS, US_STATES } from "./order/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MENU_IMAGE = "/individual-menu.png";

const inputCls = "bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white h-11 font-montserrat text-sm placeholder:text-white/30";

// ─── Egg Order Modal (questionnaire WITHOUT jewelry) ───
function EggOrderModal({ option, onClose }) {
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    billing_address: "", billing_city: "", billing_state: "", billing_zip: "",
    favorite_color: "", purse_type: "", favorite_designer: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["customer_name", "email", "phone", "billing_address", "billing_city", "billing_state", "billing_zip"];
    for (const f of required) if (!form[f].trim()) { toast.error("Please fill all required fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, {
        ...form,
        billing_country: "United States",
        shipping_same_as_billing: true,
        package_id: option.id,
      });
      toast.success("Order received! Opening secure payment...");
      window.location.href = option.link;
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" data-testid="egg-order-modal">
      <form onSubmit={submit} className="glass-card border border-[#D4AF37]/30 rounded-2xl w-full max-w-xl relative flex flex-col" style={{ maxHeight: "92vh" }}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white z-10 bg-black/50 rounded-full p-1.5">
          <XIcon className="w-4 h-4" />
        </button>
        <div className="px-5 pt-5 pb-3 border-b border-white/5">
          <h2 className="font-playfair text-[#D4AF37] text-lg sm:text-xl">{option.label}</h2>
          <p className="font-montserrat text-white/40 text-[11px] mt-0.5">${option.price} — reveals {option.reveals}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input data-testid="egg-name" placeholder="Full name *" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inputCls} />
            <Input data-testid="egg-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
            <Input data-testid="egg-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls + " sm:col-span-2"} />
          </div>

          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider">Shipping address</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Street address *" value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className={inputCls + " sm:col-span-2"} />
            <Input placeholder="City *" value={form.billing_city} onChange={(e) => set("billing_city", e.target.value)} className={inputCls} />
            <Select value={form.billing_state} onValueChange={(v) => set("billing_state", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="State *" /></SelectTrigger>
              <SelectContent>{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Zip *" value={form.billing_zip} onChange={(e) => set("billing_zip", e.target.value)} className={inputCls + " sm:col-span-2"} />
          </div>

          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider">Purse preferences</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Favorite color" value={form.favorite_color} onChange={(e) => set("favorite_color", e.target.value)} className={inputCls} />
            <Select value={form.purse_type} onValueChange={(v) => set("purse_type", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="Purse type" /></SelectTrigger>
              <SelectContent>{PURSE_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.favorite_designer} onValueChange={(v) => set("favorite_designer", v)}>
              <SelectTrigger className={inputCls + " sm:col-span-2"}><SelectValue placeholder="Favorite designer" /></SelectTrigger>
              <SelectContent>{DESIGNERS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/5 flex justify-end gap-2 bg-black/30 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full text-white/50 text-sm font-montserrat hover:text-white">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="egg-pay-btn" className="btn-gold px-6 py-2 rounded-full text-sm font-montserrat disabled:opacity-50">
            {submitting ? "Submitting..." : `Pay $${option.price}`}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Scoop Order Modal (NO questionnaire) ───
function ScoopOrderModal({ option, onClose }) {
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    billing_address: "", billing_city: "", billing_state: "", billing_zip: "",
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = Object.keys(form);
    for (const f of required) if (!form[f].trim()) { toast.error("Please fill all required fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, {
        ...form,
        billing_country: "United States",
        shipping_same_as_billing: true,
        package_id: option.id,
        favorite_color: "", purse_type: "", favorite_designer: "",
        notes,
      });
      toast.success("Order received! Opening secure payment...");
      window.location.href = option.link;
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" data-testid="scoop-order-modal">
      <form onSubmit={submit} className="glass-card border border-[#D4AF37]/30 rounded-2xl w-full max-w-md relative flex flex-col" style={{ maxHeight: "92vh" }}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white z-10 bg-black/50 rounded-full p-1.5">
          <XIcon className="w-4 h-4" />
        </button>
        <div className="px-5 pt-5 pb-3 border-b border-white/5">
          <h2 className="font-playfair text-[#D4AF37] text-lg sm:text-xl">{option.label}</h2>
          <p className="font-montserrat text-white/40 text-[11px] mt-0.5">${option.price} — reveals {option.reveals}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <Input data-testid="scoop-name" placeholder="Full name *" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inputCls} />
          <Input data-testid="scoop-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          <Input data-testid="scoop-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider pt-2">Shipping address</p>
          <Input placeholder="Street address *" value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className={inputCls} />
          <Input placeholder="City *" value={form.billing_city} onChange={(e) => set("billing_city", e.target.value)} className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.billing_state} onValueChange={(v) => set("billing_state", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="State *" /></SelectTrigger>
              <SelectContent>{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Zip *" value={form.billing_zip} onChange={(e) => set("billing_zip", e.target.value)} className={inputCls} />
          </div>

          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider pt-2">Additional information</p>
          <Textarea
            data-testid="scoop-notes"
            placeholder="Any extra details, allergies, special requests, or notes for Vicki Ann..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white font-montserrat text-sm placeholder:text-white/30 min-h-[90px]"
          />
        </div>

        <div className="px-5 py-3 border-t border-white/5 flex justify-end gap-2 bg-black/30 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full text-white/50 text-sm font-montserrat hover:text-white">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="scoop-pay-btn" className="btn-gold px-6 py-2 rounded-full text-sm font-montserrat disabled:opacity-50">
            {submitting ? "Submitting..." : `Pay $${option.price}`}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Card ───
function OptionCard({ item, Icon, i, onPick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: i * 0.08 }}
      data-testid={`individual-package-${item.id}`}
      className="relative group rounded-xl border border-[#D4AF37]/20 bg-[#0f0f0f] hover:border-[#D4AF37] transition-colors duration-500 p-6 flex flex-col"
    >
      <div className="flex items-center gap-2 mb-4">
        {Array.from({ length: Math.min(item.qty, 4) }).map((_, k) => (
          <Icon key={k} className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
        ))}
      </div>
      <p className="font-playfair text-white text-lg font-semibold mb-1">{item.label}</p>
      <p className="font-montserrat text-white/50 text-xs mb-4">Reveals {item.reveals}</p>
      <div className="gold-line w-full mb-4" />
      <div className="flex items-baseline gap-1 mb-4">
        <span className="font-playfair text-[#D4AF37] text-3xl font-bold">${item.price}</span>
      </div>
      <button
        onClick={() => onPick(item)}
        data-testid={`order-${item.id}-btn`}
        className="btn-gold w-full py-2.5 rounded-full text-xs font-montserrat tracking-wider mt-auto"
      >
        Order Now
      </button>
    </motion.div>
  );
}

// ─── Section ───
export default function IndividualMenuSection() {
  const [activeEgg, setActiveEgg] = useState(null);
  const [activeScoop, setActiveScoop] = useState(null);

  return (
    <section id="individual-menu" data-testid="individual-menu-section" className="py-24 md:py-28 relative">
      <div className="absolute inset-0 section-glow-pink" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="font-great-vibes text-[#FF007F] text-2xl md:text-3xl mb-3">Pick Your Mystery</p>
          <h2 className="font-playfair text-[#D4AF37] text-4xl md:text-5xl font-bold">Golden Eggs &amp; Pearl Scoops</h2>
          <div className="gold-line w-24 mx-auto mt-6" />
          <p className="font-montserrat text-white/40 text-sm mt-6 max-w-xl mx-auto">
            Prefer just a few eggs or just a scoop? Pick the exact amount of mystery you want.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto mb-16 rounded-2xl overflow-hidden border border-[#D4AF37]/15"
        >
          <img src={MENU_IMAGE} alt="Luxurious Mystery Scoop Offers" className="w-full h-auto object-cover" />
        </motion.div>

        <div className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <Egg className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-playfair text-white text-xl">Golden Eggs <span className="text-white/30 text-base">— Purses</span></h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {EGG_OPTIONS.map((e, i) => <OptionCard key={e.id} item={e} Icon={Egg} i={i} onPick={setActiveEgg} />)}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-6">
            <IceCream2 className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-playfair text-white text-xl">Scoops of Pearls <span className="text-white/30 text-base">— Accessories</span></h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
            {SCOOP_OPTIONS.map((s, i) => <OptionCard key={s.id} item={s} Icon={IceCream2} i={i} onPick={setActiveScoop} />)}
          </div>
        </div>
      </div>

      {activeEgg && <EggOrderModal option={activeEgg} onClose={() => setActiveEgg(null)} />}
      {activeScoop && <ScoopOrderModal option={activeScoop} onClose={() => setActiveScoop(null)} />}
    </section>
  );
}
